import { useState, useRef } from 'react';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';


// Resume Builder — form + live preview + PDF download (html2canvas + jsPDF).
// Runs fully in the browser. Photo optional. Inline colors so PDF captures fine.
// v5: 6 templates (Classic, Professional, Modern, Minimal, Elegant, ATS)
//     + Projects section + Hobbies/Interests section
//     + Accent color picker
//     + Empty fields with placeholders. Preview shows a faded DEMO that is
//       overwritten field-by-field the moment the user types.

const BLANK_EXP  = { role:'', company:'', duration:'', desc:'' };
const BLANK_EDU  = { degree:'', institute:'', year:'', score:'' };
const BLANK_CERT = { name:'', year:'', code:'' };
const BLANK_PROJ = { name:'', desc:'', link:'' };

// Demo/placeholder content. Shown faded in the preview when a field is empty,
// and used as the input placeholder text so users know what to write.
const DEMO = {
  name:'John Doe', title:'Full Stack Developer',
  email:'john.doe@email.com', phone:'+91 98765 43210',
  location:'Bengaluru, India',
  dob:'12 March 2000', nationality:'Indian',
  website:'johndoe.dev',
  github:'github.com/johndoe', linkedin:'linkedin.com/in/johndoe',
  summary:'Passionate Full Stack Developer with 3+ years of experience building scalable web applications. Skilled in React, Node.js, and cloud technologies. A strong problem-solver who enjoys turning complex ideas into clean, user-friendly products.',
  experience:[
    { role:'Full Stack Developer', company:'TechNova Solutions', duration:'2023 - Present', desc:'Built and maintained 10+ client web applications using React and Node.js. Improved page load speed by 40% and led a team of 3 junior developers.' },
    { role:'Frontend Developer Intern', company:'WebCraft Studio', duration:'2022 - 2023', desc:'Developed responsive user interfaces and collaborated with designers to deliver pixel-perfect websites for 5 major clients.' },
  ],
  education:[
    { degree:'B.Tech, Computer Science', institute:'RV College of Engineering', year:'2018 - 2022', score:'8.7 CGPA' },
    { degree:'Class XII (CBSE)', institute:'Delhi Public School', year:'2018', score:'92%' },
  ],
  skills:'React, Node.js, JavaScript, Python, MongoDB, AWS',
  certificates:[
    { name:'AWS Certified Developer', year:'2023', code:'AWS-DEV-2023-4521' },
  ],
  projects:[
    { name:'DevBoard — Task Manager', desc:'A real-time kanban board built with React and Socket.io. Supports drag-and-drop, live collaboration, and role-based access.', link:'github.com/johndoe/devboard' },
    { name:'ShopEasy — E-commerce API', desc:'REST API for an online store with cart, payments (Razorpay), and admin dashboard. Built on Node.js, Express, and MongoDB.', link:'github.com/johndoe/shopeasy' },
  ],
  hobbies:'Photography, Chess, Open-source contribution, Travelling',
};

// Accent color choices for the picker (Resume-Now style circles).
const ACCENTS = [
  { id:'navy',      color:'#1E3A5F', label:'Navy' },
  { id:'blue',      color:'#3B82C4', label:'Blue' },
  { id:'teal',      color:'#0E9488', label:'Teal' },
  { id:'green',     color:'#2E7D5B', label:'Green' },
  { id:'amber',     color:'#D97706', label:'Amber' },
  { id:'red',       color:'#C0392B', label:'Red' },
  { id:'purple',    color:'#6D28D9', label:'Purple' },
  { id:'charcoal',  color:'#2B2F36', label:'Charcoal' },
];

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { if (existing.dataset.loaded) return resolve(); existing.addEventListener('load', resolve); existing.addEventListener('error', reject); return; }
    const sc = document.createElement('script');
    sc.src = src;
    sc.onload = () => { sc.dataset.loaded = '1'; resolve(); };
    sc.onerror = () => reject(new Error('load failed: ' + src));
    document.body.appendChild(sc);
  });
}

// Module-level flag: when true (preview), empty fields fall back to faded demo
// content. When false (PDF export), empty fields stay truly empty so the demo
// never appears in the downloaded resume.
let DEMO_ON = true;

// Helper: returns { value, isDemo }. If the user left a field blank:
//   - in preview (DEMO_ON=true)  -> show faded demo text
//   - in export  (DEMO_ON=false) -> show nothing (empty)
function show(actual, demo) {
  const v = (actual || '').trim();
  if (v) return { value:actual, isDemo:false };
  return { value: DEMO_ON ? (demo || '') : '', isDemo:true };
}

export default function ResumeBuilder() {
  const [data, setData] = useState({
    name:'', title:'',
    email:'', phone:'',
    location:'', website:'',
    dob:'', nationality:'',
    github:'', linkedin:'',
    photo:'',
    summary:'',
    experience:[ {...BLANK_EXP}, {...BLANK_EXP} ],
    education:[ {...BLANK_EDU}, {...BLANK_EDU} ],
    skills:'',
    certificates:[ {...BLANK_CERT} ],
    projects:[ {...BLANK_PROJ}, {...BLANK_PROJ} ],
    hobbies:'',
  });
  const [template, setTemplate] = useState('classic');
  const [accent, setAccent] = useState('navy');
  const [downloading, setDownloading] = useState(false);
  // When the user clicks download with required fields empty, we flip this on
  // so those fields turn red and a friendly message appears — no jarring alert.
  const [showErrors, setShowErrors] = useState(false);
  // demoOn drives whether blank fields show faded demo content. It is true for
  // the on-screen preview and flipped to false only while generating the PDF.
  const [demoOn, setDemoOn] = useState(true);
  const previewRef = useRef(null);

  // Keep the module-level flag (used by show()/rowsOrDemo()) in sync every render.
  DEMO_ON = demoOn;

  const accentColor = (ACCENTS.find(a => a.id === accent) || ACCENTS[0]).color;

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const setExp = (i,k,v) => setData(d => { const e=[...d.experience]; e[i]={...e[i],[k]:v}; return {...d,experience:e}; });
  const addExp = () => setData(d => ({ ...d, experience:[...d.experience,{...BLANK_EXP}] }));
  const delExp = i => setData(d => ({ ...d, experience:d.experience.filter((_,x)=>x!==i) }));
  const setEdu = (i,k,v) => setData(d => { const e=[...d.education]; e[i]={...e[i],[k]:v}; return {...d,education:e}; });
  const addEdu = () => setData(d => ({ ...d, education:[...d.education,{...BLANK_EDU}] }));
  const delEdu = i => setData(d => ({ ...d, education:d.education.filter((_,x)=>x!==i) }));
  const setCert = (i,k,v) => setData(d => { const c=[...d.certificates]; c[i]={...c[i],[k]:v}; return {...d,certificates:c}; });
  const addCert = () => setData(d => ({ ...d, certificates:[...d.certificates,{...BLANK_CERT}] }));
  const delCert = i => setData(d => ({ ...d, certificates:d.certificates.filter((_,x)=>x!==i) }));
  const setProj = (i,k,v) => setData(d => { const p=[...d.projects]; p[i]={...p[i],[k]:v}; return {...d,projects:p}; });
  const addProj = () => setData(d => ({ ...d, projects:[...d.projects,{...BLANK_PROJ}] }));
  const delProj = i => setData(d => ({ ...d, projects:d.projects.filter((_,x)=>x!==i) }));

  const onPhoto = e => {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => set('photo', r.result);
    r.readAsDataURL(f);
  };

  // Skills: user's if typed; else demo (preview only). Empty in export.
  const skillsAreDemo = !data.skills.trim();
  const skillsRaw = !skillsAreDemo ? data.skills : (demoOn ? DEMO.skills : '');
  const skillList = skillsRaw.split(',').map(s => s.trim()).filter(Boolean);

  const hobbiesAreDemo = !data.hobbies.trim();
  const hobbiesRaw = !hobbiesAreDemo ? data.hobbies : (demoOn ? DEMO.hobbies : '');
  const hobbyList = hobbiesRaw.split(',').map(s => s.trim()).filter(Boolean);

  const downloadPdf = async () => {
    // Validate required fields before generating the PDF
    // Clean validation: instead of a jarring popup, highlight the empty
    // required fields in red and scroll the user up to fix them.
    if (anyMissing) {
      setShowErrors(true);
      if (previewRef.current) window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setShowErrors(false);

    setDownloading(true);
    // Hide all demo/placeholder content so only the user's real data is exported.
    setDemoOn(false);
    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      const html2canvas = window.html2canvas;
      const jsPDF = (window.jspdf && window.jspdf.jsPDF) || window.jsPDF;
      if (!html2canvas || !jsPDF) throw new Error('PDF libraries not available');

      const el = previewRef.current;
      // Wait for React to re-render the preview without the demo content.
      await new Promise(r => setTimeout(r, 200));

      // Capture at 2x for sharpness, but force the element to render at
      // exactly 794px (A4 at 96 dpi) so font sizes are consistent regardless
      // of the browser window width.
      const origWidth = el.style.width;
      el.style.width = '794px';
      await new Promise(r => setTimeout(r, 80)); // let layout reflow

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: 794,
        windowWidth: 794,
      });

      el.style.width = origWidth; // restore
      const imgData = canvas.toDataURL('image/jpeg', 0.98);

      // Always produce a standard A4 page (210 x 297 mm) so the resume looks
      // consistent no matter how much content there is. Short resumes get white
      // space at the bottom (like a real one-page resume) instead of a tiny,
      // zoomed-in page. Long resumes flow onto additional A4 pages.
      const A4_W = 210;
      const A4_H = 297;
      const imgW = A4_W;
      const imgH = (canvas.height * imgW) / canvas.width;

      const pdf = new jsPDF({ unit:'mm', format:'a4', orientation:'portrait' });

      // Strategy:
      // 1) Content fits in 1 page (with small tolerance) → place as-is
      // 2) Content is up to 1.3x A4 height → scale down to fit 1 page
      //    (avoids ugly splits for slightly-over resumes)
      // 3) Genuinely long → multi-page with clean page breaks
      const SHRINK_LIMIT = A4_H * 1.3;

      if (imgH <= A4_H + 5) {
        // Fits cleanly — place at top
        pdf.addImage(imgData, 'JPEG', 0, 0, imgW, Math.min(imgH, A4_H));
      } else if (imgH <= SHRINK_LIMIT) {
        // Slightly over — scale down to fit 1 page, centered horizontally
        const ratio = A4_H / imgH;
        const scaledW = imgW * ratio;
        const offsetX = (A4_W - scaledW) / 2;
        pdf.addImage(imgData, 'JPEG', offsetX, 0, scaledW, A4_H);
      } else {
        // Genuinely multi-page — slice with clean page breaks
        let yOffset = 0;
        let remaining = imgH;
        pdf.addImage(imgData, 'JPEG', 0, -yOffset, imgW, imgH);
        remaining -= A4_H;
        while (remaining > 5) {
          yOffset += A4_H;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, -yOffset, imgW, imgH);
          remaining -= A4_H;
        }
      }
      pdf.save(`${(data.name || 'resume').replace(/\s+/g, '_')}.pdf`);
    } catch (e) {
      alert('Sorry, PDF download failed. Please check your internet connection and try again.');
    }
    // Bring the demo preview back for continued editing.
    setDemoOn(true);
    setDownloading(false);
  };

  const inp = { className:'form-control', style:{ marginBottom:10 } };

  // Which required fields are still empty (used to highlight them in red).
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
  const phoneValid = data.phone.replace(/\D/g, '').length >= 10;
  const hasDegree = data.education.some(e => e.degree.trim());
  const missing = {
    name: !data.name.trim(),
    email: !data.email.trim() || !emailValid,
    phone: !data.phone.trim() || !phoneValid,
    degree: !hasDegree,
  };
  const anyMissing = missing.name || missing.email || missing.phone || missing.degree;

  // Red border style applied to a required field when it's empty AND the user
  // has already tried to download (so we don't nag before they've started).
  const errStyle = (isBad) => (showErrors && isBad)
    ? { borderColor:'#DC2626', boxShadow:'0 0 0 3px rgba(220,38,38,.12)' }
    : {};

  // Props passed to every template. Demo fallbacks + accent live here so each
  // template stays simple.
  const tplProps = {
    data, accentColor,
    skillList, skillsAreDemo,
    hobbyList, hobbiesAreDemo,
  };

  return (
    <>
    <Seo title="Free Resume Builder — Download PDF" description="Build a professional resume online and download as PDF, free. Choose from multiple templates and colors. No signup." path="/tools/utility/resume-builder" />
    <div className="tool-pg">
      <div className="container">
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon cat-text"><Icon name="user" size={24} /></div>
            <div>
              <h1>Resume Builder</h1>
              <p>Fill in your details, pick a template and color, and download a clean PDF resume. Free and private — nothing is uploaded.</p>
            </div>
          </div>
        </div>

        <div className="resume-layout">
          {/* FORM */}
          <div className="resume-form">
            <h3 className="resume-form-sec">Personal Details</h3>
            <p style={{ fontSize:12, color:'#76828F', margin:'-4px 0 10px' }}>Fields marked <span style={{ color:'#DC2626', fontWeight:700 }}>*</span> are required. The preview shows a sample resume — start typing and it updates live.</p>
            <input {...inp} style={{ ...inp.style, ...errStyle(missing.name) }} placeholder="Full Name *" value={data.name} onChange={e => set('name', e.target.value)} />
            {showErrors && missing.name && <p style={{ fontSize:12, color:'#DC2626', margin:'-6px 0 10px' }}>Full name is required.</p>}
            <input {...inp} placeholder="Job Title (e.g. Web Developer)" value={data.title} onChange={e => set('title', e.target.value)} />
            <input {...inp} style={{ ...inp.style, ...errStyle(missing.email) }} placeholder="Email *" value={data.email} onChange={e => set('email', e.target.value)} inputMode="email" />
            {showErrors && !data.email.trim() && <p style={{ fontSize:12, color:'#DC2626', margin:'-6px 0 10px' }}>Email is required.</p>}
            {data.email.trim() && !emailValid && <p style={{ fontSize:12, color:'#DC2626', margin:'-6px 0 10px' }}>Please enter a valid email (e.g. name@email.com).</p>}
            <input {...inp} style={{ ...inp.style, ...errStyle(missing.phone) }} placeholder="Phone *" value={data.phone} onChange={e => set('phone', e.target.value.replace(/[^0-9+\-() ]/g, ''))} inputMode="tel" />
            {showErrors && !data.phone.trim() && <p style={{ fontSize:12, color:'#DC2626', margin:'-6px 0 10px' }}>Phone number is required.</p>}
            {data.phone.trim() && !phoneValid && <p style={{ fontSize:12, color:'#DC2626', margin:'-6px 0 10px' }}>Phone number should have at least 10 digits.</p>}
            <input {...inp} placeholder="Location (e.g. Indore, India)" value={data.location} onChange={e => set('location', e.target.value)} />
            <input {...inp} placeholder="Date of Birth (e.g. 15 Aug 2000)" value={data.dob} onChange={e => set('dob', e.target.value)} />
            <input {...inp} placeholder="Nationality (e.g. Indian)" value={data.nationality} onChange={e => set('nationality', e.target.value)} />

            <div style={{ margin:'6px 0 16px' }}>
              <label style={{ fontSize:13, fontWeight:600, color:'var(--slate-700)', display:'block', marginBottom:6 }}>Photo (optional)</label>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer' }}>
                  <Icon name="upload" size={14} />Choose Photo
                  <input type="file" accept="image/*" hidden onChange={onPhoto} />
                </label>
                {data.photo && <button className="btn btn-outline btn-sm" onClick={() => set('photo','')}><Icon name="trash" size={14} />Remove</button>}
              </div>
            </div>

            <h3 className="resume-form-sec">Professional Summary</h3>
            <textarea {...inp} rows={3} placeholder="A short 2-3 line summary about yourself..." value={data.summary} onChange={e => set('summary', e.target.value)} style={{ marginBottom:10, resize:'vertical' }} />

            <h3 className="resume-form-sec">Work Experience</h3>
            {data.experience.map((ex, i) => (
              <div key={i} className="resume-form-card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'var(--slate-400)' }}>EXPERIENCE {i+1}</span>
                  {data.experience.length > 1 && <button className="icon-btn" onClick={() => delExp(i)}><Icon name="trash" size={14} /></button>}
                </div>
                <input {...inp} placeholder="Role / Position" value={ex.role} onChange={e => setExp(i,'role',e.target.value)} />
                <input {...inp} placeholder="Company" value={ex.company} onChange={e => setExp(i,'company',e.target.value)} />
                <input {...inp} placeholder="Duration (e.g. 2022 - Present)" value={ex.duration} onChange={e => setExp(i,'duration',e.target.value)} />
                <textarea {...inp} rows={2} placeholder="What you did / achievements..." value={ex.desc} onChange={e => setExp(i,'desc',e.target.value)} style={{ marginBottom:0, resize:'vertical' }} />
              </div>
            ))}
            <button className="btn btn-outline btn-sm" onClick={addExp}><Icon name="plus" size={14} />Add Experience</button>

            <h3 className="resume-form-sec" style={{ marginTop:20 }}>Education</h3>
            {data.education.map((ed, i) => (
              <div key={i} className="resume-form-card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'var(--slate-400)' }}>EDUCATION {i+1}</span>
                  {data.education.length > 1 && <button className="icon-btn" onClick={() => delEdu(i)}><Icon name="trash" size={14} /></button>}
                </div>
                <input {...inp} style={{ ...inp.style, ...errStyle(missing.degree && i === 0) }} placeholder="Degree (e.g. B.Tech Computer Science) *" value={ed.degree} onChange={e => setEdu(i,'degree',e.target.value)} />
                {showErrors && missing.degree && i === 0 && <p style={{ fontSize:12, color:'#DC2626', margin:'-6px 0 10px' }}>At least one degree is required.</p>}
                <input {...inp} placeholder="Institute / University" value={ed.institute} onChange={e => setEdu(i,'institute',e.target.value)} />
                <input {...inp} placeholder="Year (e.g. 2018 - 2022)" value={ed.year} onChange={e => setEdu(i,'year',e.target.value)} />
                <input {...inp} placeholder="Percentage / CGPA (e.g. 8.5 CGPA or 82%)" value={ed.score} onChange={e => setEdu(i,'score',e.target.value)} style={{ marginBottom:0 }} />
              </div>
            ))}
            <button className="btn btn-outline btn-sm" onClick={addEdu}><Icon name="plus" size={14} />Add Education</button>

            <h3 className="resume-form-sec" style={{ marginTop:20 }}>Projects</h3>
            {data.projects.map((pr, i) => (
              <div key={i} className="resume-form-card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'var(--slate-400)' }}>PROJECT {i+1}</span>
                  {data.projects.length > 1 && <button className="icon-btn" onClick={() => delProj(i)}><Icon name="trash" size={14} /></button>}
                </div>
                <input {...inp} placeholder="Project Name (e.g. Task Manager App)" value={pr.name} onChange={e => setProj(i,'name',e.target.value)} />
                <textarea {...inp} rows={2} placeholder="Short description — what it does, tech used..." value={pr.desc} onChange={e => setProj(i,'desc',e.target.value)} style={{ resize:'vertical' }} />
                <input {...inp} placeholder="Link (e.g. github.com/username/project)" value={pr.link} onChange={e => setProj(i,'link',e.target.value)} style={{ marginBottom:0 }} />
              </div>
            ))}
            <button className="btn btn-outline btn-sm" onClick={addProj}><Icon name="plus" size={14} />Add Project</button>

            <h3 className="resume-form-sec" style={{ marginTop:20 }}>Links</h3>
            <input {...inp} placeholder="Website / Portfolio (optional)" value={data.website} onChange={e => set('website', e.target.value)} />
            <input {...inp} placeholder="GitHub (e.g. github.com/username)" value={data.github} onChange={e => set('github', e.target.value)} />
            <input {...inp} placeholder="LinkedIn (e.g. linkedin.com/in/username)" value={data.linkedin} onChange={e => set('linkedin', e.target.value)} style={{ marginBottom:0 }} />

            <h3 className="resume-form-sec" style={{ marginTop:20 }}>Skills</h3>
            <input {...inp} placeholder="Comma separated (e.g. React, Node, Python)" value={data.skills} onChange={e => set('skills', e.target.value)} style={{ marginBottom:0 }} />

            <h3 className="resume-form-sec" style={{ marginTop:20 }}>Hobbies / Interests</h3>
            <input {...inp} placeholder="Comma separated (e.g. Photography, Chess, Travelling)" value={data.hobbies} onChange={e => set('hobbies', e.target.value)} style={{ marginBottom:0 }} />

            <h3 className="resume-form-sec" style={{ marginTop:20 }}>Certificates</h3>
            {data.certificates.map((ct, i) => (
              <div key={i} className="resume-form-card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'var(--slate-400)' }}>CERTIFICATE {i+1}</span>
                  {data.certificates.length > 1 && <button className="icon-btn" onClick={() => delCert(i)}><Icon name="trash" size={14} /></button>}
                </div>
                <input {...inp} placeholder="Certificate Name (e.g. AWS Certified Developer)" value={ct.name} onChange={e => setCert(i,'name',e.target.value)} />
                <input {...inp} placeholder="Year (e.g. 2023)" value={ct.year} onChange={e => setCert(i,'year',e.target.value)} />
                <input {...inp} placeholder="Certificate Code / ID (e.g. AWS-DEV-2023-4521)" value={ct.code} onChange={e => setCert(i,'code',e.target.value)} style={{ marginBottom:0 }} />
              </div>
            ))}
            <button className="btn btn-outline btn-sm" onClick={addCert}><Icon name="plus" size={14} />Add Certificate</button>

            {showErrors && anyMissing && (
              <div style={{
                display:'flex', alignItems:'center', gap:8, marginTop:24,
                padding:'11px 14px', borderRadius:8,
                background:'#FEF2F2', border:'1px solid #FCA5A5', color:'#991B1B',
                fontSize:13.5, fontWeight:500,
              }}>
                <Icon name="alertCircle" size={16} />
                Please fill the required fields marked in red above, then download.
              </div>
            )}

            <button className="btn btn-primary btn-lg btn-full" style={{ marginTop: showErrors && anyMissing ? 12 : 24 }} onClick={downloadPdf} disabled={downloading}>
              {downloading ? <><span className="spinner sp-sm" />Preparing PDF...</> : <><Icon name="download" size={18} />Download PDF</>}
            </button>
          </div>

          {/* PREVIEW */}
          <div className="resume-preview-wrap">
            {/* Template chooser */}
            <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
              {[
                { id:'classic', label:'Classic' },
                { id:'professional', label:'Professional' },
                { id:'modern', label:'Modern' },
                { id:'minimal', label:'Minimal' },
                { id:'elegant', label:'Elegant' },
                { id:'ats', label:'ATS' },
              ].map(t => (
                <button key={t.id}
                  onClick={() => setTemplate(t.id)}
                  style={{
                    padding:'7px 16px', borderRadius:8, cursor:'pointer',
                    fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:600,
                    border: template===t.id ? '2px solid #1E3A5F' : '1.5px solid #E3E8ED',
                    background: template===t.id ? '#1E3A5F' : '#fff',
                    color: template===t.id ? '#fff' : '#566377',
                    transition:'all .15s',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Accent color chooser (hidden for ATS — it is intentionally plain) */}
            {template !== 'ats' && (
              <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
                <span style={{ fontSize:12, fontWeight:600, color:'#76828F', fontFamily:"'Sora',sans-serif" }}>Color:</span>
                {ACCENTS.map(a => (
                  <button key={a.id}
                    onClick={() => setAccent(a.id)}
                    title={a.label}
                    aria-label={a.label}
                    style={{
                      width:26, height:26, borderRadius:'50%', cursor:'pointer',
                      background:a.color, padding:0,
                      border: accent===a.id ? '2px solid #1A2433' : '2px solid #fff',
                      outline: accent===a.id ? '2px solid #1A2433' : '1px solid #E3E8ED',
                      transition:'transform .12s',
                      transform: accent===a.id ? 'scale(1.12)' : 'scale(1)',
                    }} />
                ))}
              </div>
            )}

            <div ref={previewRef}>
              {template === 'classic'      && <ClassicTemplate      {...tplProps} />}
              {template === 'professional' && <ProfessionalTemplate {...tplProps} />}
              {template === 'modern'       && <ModernTemplate       {...tplProps} />}
              {template === 'minimal'      && <MinimalTemplate      {...tplProps} />}
              {template === 'elegant'      && <ElegantTemplate      {...tplProps} />}
              {template === 'ats'          && <AtsTemplate          {...tplProps} />}
            </div>
          </div>
        </div>

        <div className="alert alert-info" style={{ marginTop:20 }}>
          <Icon name="info" size={16} />
          <span>The preview shows sample content in grey — it's only a guide. As you fill each field, your real details replace the sample instantly. Nothing is uploaded or saved on a server.</span>
        </div>
        <ToolSeoContent tool="resume-builder" />
      </div>
    </div>
  </>
  );
}

/* ---------------------------------------------------------------------------
   Shared helpers for templates. Each template receives the raw `data` plus
   demo fallbacks. `f(actual, demo)` returns the text to show and a flag for
   whether it is filler, so we can fade it.
--------------------------------------------------------------------------- */

// Returns filtered rows: rows where the user typed something -> real rows.
// If the user typed NOTHING across all rows, returns the demo rows (as demo).
function rowsOrDemo(userRows, demoRows, keys) {
  const filled = userRows.filter(r => keys.some(k => (r[k] || '').trim()));
  if (filled.length) return filled.map(r => ({ row:r, isDemo:false }));
  // No user rows: show demo only in preview, nothing in the exported PDF.
  if (!DEMO_ON) return [];
  return demoRows.map(r => ({ row:r, isDemo:true }));
}

const fade = (isDemo) => isDemo ? 0.4 : 1;

/* ============================ TEMPLATE 1: CLASSIC ============================ */
function ClassicTemplate({ data, accentColor, skillList, skillsAreDemo, hobbyList, hobbiesAreDemo }) {
  const C = { ink:'#1A2433', slate8:'#293548', slate6:'#566377', slate5:'#76828F', slate4:'#A0AAB5', slate2:'#E3E8ED', white:'#FFFFFF' };
  const A = accentColor;
  const nm = show(data.name, DEMO.name), tt = show(data.title, DEMO.title);
  const em = show(data.email, DEMO.email), ph = show(data.phone, DEMO.phone), lc = show(data.location, DEMO.location);
  const db = show(data.dob, DEMO.dob), nat = show(data.nationality, DEMO.nationality);
  const sm = show(data.summary, DEMO.summary);
  const ws = show(data.website, DEMO.website), gh = show(data.github, DEMO.github), li = show(data.linkedin, DEMO.linkedin);
  const linksDemo = !(data.website.trim() || data.github.trim() || data.linkedin.trim());
  const exp = rowsOrDemo(data.experience, DEMO.experience, ['role','company','duration','desc']);
  const edu = rowsOrDemo(data.education, DEMO.education, ['degree','institute','year','score']);
  const cert = rowsOrDemo(data.certificates, DEMO.certificates, ['name','year','code']);
  const proj = rowsOrDemo(data.projects, DEMO.projects, ['name','desc','link']);

  const S = {
    sheet:{ position:'relative', overflow:'hidden', background:C.white, width:'100%', maxWidth:800, margin:'0 auto', color:C.ink, padding:'44px 44px 56px', fontFamily:"Georgia,'Times New Roman',serif", lineHeight:1.65, borderRadius:12, border:`1.5px solid ${C.slate2}`, boxShadow:'0 4px 6px rgba(0,0,0,.06)' },
    name:{ fontSize:28, fontWeight:700, color:C.ink, marginBottom:6, fontFamily:"'Sora',sans-serif", letterSpacing:'-.5px' },
    title:{ fontSize:15, color:A, fontFamily:"'Sora',sans-serif", fontWeight:600 },
    contact:{ fontSize:13, color:C.slate5, marginBottom:6, fontFamily:"'Sora',sans-serif", display:'flex', flexWrap:'wrap', gap:'4px 12px' },
    divider:{ height:2, background:A, borderRadius:2, margin:'18px 0' },
    secTtl:{ fontSize:11, fontWeight:700, color:A, textTransform:'uppercase', letterSpacing:'1.5px', marginBottom:12, fontFamily:"'Sora',sans-serif", borderBottom:`1px solid ${C.slate2}`, paddingBottom:6 },
    expRole:{ fontSize:14, fontWeight:700, color:C.slate8, fontFamily:"'Sora',sans-serif" },
    expDur:{ fontSize:12, color:C.slate4, fontFamily:"'Sora',sans-serif" },
    expCo:{ fontSize:13, color:A, marginBottom:4, fontFamily:"'Sora',sans-serif", fontStyle:'italic' },
    expDesc:{ fontSize:13, color:C.slate6, lineHeight:1.6 },
    eduDeg:{ fontSize:14, fontWeight:600, color:C.slate8, fontFamily:"'Sora',sans-serif" },
    eduInst:{ fontSize:13, color:C.slate5, fontFamily:"'Sora',sans-serif" },
    eduYr:{ fontSize:12, color:C.slate4, fontFamily:"'Sora',sans-serif", whiteSpace:'nowrap', textAlign:'right' },
    eduScore:{ fontSize:12, color:C.slate5, fontFamily:"'Sora',sans-serif", whiteSpace:'nowrap', textAlign:'right' },
    skill:{ color:C.slate8, fontSize:13, fontFamily:"'Sora',sans-serif", fontWeight:500 },
    infoLabel:{ fontWeight:600, color:C.slate8 },
    linkRow:{ fontSize:13, color:C.slate6, fontFamily:"'Sora',sans-serif", marginBottom:4 },
    watermark:{ position:'absolute', bottom:16, right:20, fontSize:11, color:'#C2CEDB', fontFamily:"'Sora',sans-serif", fontWeight:600, letterSpacing:'.5px', pointerEvents:'none' },
  };
  return (
    <div style={S.sheet}>
      <div style={{ display:'flex', gap:18, alignItems:'center', marginBottom:4 }}>
        {data.photo && <img src={data.photo} alt="" style={{ width:84, height:84, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:`2px solid ${C.slate2}` }} />}
        <div>
          <div style={{ ...S.name, opacity:fade(nm.isDemo) }}>{nm.value}</div>
          <div style={{ ...S.title, opacity:fade(tt.isDemo) }}>{tt.value}</div>
        </div>
      </div>
      <div style={S.contact}>
        <span style={{ opacity:fade(em.isDemo) }}>{em.value}</span>
        <span style={{ opacity:fade(ph.isDemo) }}>· {ph.value}</span>
        <span style={{ opacity:fade(lc.isDemo) }}>· {lc.value}</span>
      </div>
      {(db.value || nat.value) && <div style={{ ...S.contact, marginBottom:0 }}>
        {db.value && <span style={{ opacity:fade(db.isDemo) }}><span style={S.infoLabel}>DOB:</span> {db.value}</span>}
        {nat.value && <span style={{ opacity:fade(nat.isDemo) }}>· <span style={S.infoLabel}>Nationality:</span> {nat.value}</span>}
      </div>}
      <div style={S.divider} />
      {sm.value && <div style={{ marginBottom:18 }}><div style={S.secTtl}>Summary</div><p style={{ ...S.expDesc, opacity:fade(sm.isDemo) }}>{sm.value}</p></div>}

      {exp.length > 0 && <div style={{ marginBottom:18 }}><div style={S.secTtl}>Experience</div>
        {exp.map(({row:ex, isDemo}, i) => (
          <div key={i} style={{ marginBottom:16, opacity:fade(isDemo) }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}><span style={S.expRole}>{ex.role}</span><span style={S.expDur}>{ex.duration}</span></div>
            {ex.company && <div style={S.expCo}>{ex.company}</div>}
            {ex.desc && <div style={S.expDesc}>{ex.desc}</div>}
          </div>
        ))}
      </div>}

      {edu.length > 0 && <div style={{ marginBottom:18 }}><div style={S.secTtl}>Education</div>
        {edu.map(({row:ed, isDemo}, i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10, opacity:fade(isDemo) }}>
            <div><div style={S.eduDeg}>{ed.degree}</div><div style={S.eduInst}>{ed.institute}</div></div>
            <div>{ed.year && <div style={S.eduYr}>{ed.year}</div>}{ed.score && <div style={S.eduScore}>{ed.score}</div>}</div>
          </div>
        ))}
      </div>}

      {proj.length > 0 && <div style={{ marginBottom:18 }}><div style={S.secTtl}>Projects</div>
        {proj.map(({row:pr, isDemo}, i) => (
          <div key={i} style={{ marginBottom:12, opacity:fade(isDemo) }}>
            <div style={S.eduDeg}>{pr.name}</div>
            {pr.desc && <div style={S.expDesc}>{pr.desc}</div>}
            {pr.link && <div style={{ ...S.linkRow, color:A, marginTop:2 }}>{pr.link}</div>}
          </div>
        ))}
      </div>}

      {cert.length > 0 && <div style={{ marginBottom:18 }}><div style={S.secTtl}>Certificates</div>
        {cert.map(({row:ct, isDemo}, i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10, opacity:fade(isDemo) }}>
            <div><div style={S.eduDeg}>{ct.name}</div>{ct.code && <div style={S.eduInst}>ID: {ct.code}</div>}</div>
            {ct.year && <div style={S.eduYr}>{ct.year}</div>}
          </div>
        ))}
      </div>}

      {(ws.value || gh.value || li.value) && <div style={{ marginBottom:18 }}><div style={S.secTtl}>Links</div>
        {ws.value && <div style={{ ...S.linkRow, opacity:fade(linksDemo) }}><span style={S.infoLabel}>Website:</span> {ws.value}</div>}
        {gh.value && <div style={{ ...S.linkRow, opacity:fade(linksDemo) }}><span style={S.infoLabel}>GitHub:</span> {gh.value}</div>}
        {li.value && <div style={{ ...S.linkRow, opacity:fade(linksDemo) }}><span style={S.infoLabel}>LinkedIn:</span> {li.value}</div>}
      </div>}

      {skillList.length > 0 && <div style={{ marginBottom:18 }}><div style={S.secTtl}>Skills</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 0', opacity:fade(skillsAreDemo) }}>
          {skillList.map((s, i) => (<span key={i} style={S.skill}>{s}{i < skillList.length - 1 && <span style={{ color:C.slate4, margin:'0 10px' }}>•</span>}</span>))}
        </div>
      </div>}

      {hobbyList.length > 0 && <div><div style={S.secTtl}>Hobbies & Interests</div>
        <div style={{ ...S.skill, opacity:fade(hobbiesAreDemo) }}>{hobbyList.join(' · ')}</div>
      </div>}
      <div style={S.watermark}>Made with Devlayar</div>
    </div>
  );
}

/* ========================= TEMPLATE 2: PROFESSIONAL ========================= */
function ProfessionalTemplate({ data, accentColor, skillList, skillsAreDemo, hobbyList, hobbiesAreDemo }) {
  const navy='#0F2A47', ink='#1F2937', grey='#4B5563', light='#6B7280', line='#E5E7EB';
  const accent = accentColor;
  const F = "'Sora',-apple-system,sans-serif";
  const nm = show(data.name, DEMO.name), tt = show(data.title, DEMO.title);
  const em = show(data.email, DEMO.email), ph = show(data.phone, DEMO.phone), lc = show(data.location, DEMO.location);
  const db = show(data.dob, DEMO.dob), nat = show(data.nationality, DEMO.nationality);
  const sm = show(data.summary, DEMO.summary);
  const ws = show(data.website, DEMO.website), gh = show(data.github, DEMO.github), li = show(data.linkedin, DEMO.linkedin);
  const linksDemo = !(data.website.trim() || data.github.trim() || data.linkedin.trim());
  const exp = rowsOrDemo(data.experience, DEMO.experience, ['role','company','duration','desc']);
  const edu = rowsOrDemo(data.education, DEMO.education, ['degree','institute','year','score']);
  const cert = rowsOrDemo(data.certificates, DEMO.certificates, ['name','year','code']);
  const proj = rowsOrDemo(data.projects, DEMO.projects, ['name','desc','link']);

  const secTtl = { fontSize:12, fontWeight:700, color:navy, textTransform:'uppercase', letterSpacing:'2px', margin:'0 0 12px', fontFamily:F, position:'relative', paddingLeft:14 };
  const bar = { position:'absolute', left:0, top:2, width:5, height:14, background:accent, borderRadius:2 };
  return (
    <div style={{ position:'relative', overflow:'hidden', background:'#fff', width:'100%', maxWidth:800, margin:'0 auto', color:ink, fontFamily:F, lineHeight:1.6, borderRadius:12, border:`1.5px solid ${line}`, boxShadow:'0 4px 6px rgba(0,0,0,.06)' }}>
      <div style={{ background:navy, color:'#fff', padding:'34px 44px 26px', display:'flex', gap:20, alignItems:'center' }}>
        {data.photo && <img src={data.photo} alt="" style={{ width:88, height:88, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:'3px solid rgba(255,255,255,.3)' }} />}
        <div style={{ flex:1 }}>
          <div style={{ fontSize:30, fontWeight:700, letterSpacing:'-.5px', marginBottom:4, opacity:fade(nm.isDemo) }}>{nm.value}</div>
          <div style={{ fontSize:15, color:'#9FE5D5', fontWeight:600, letterSpacing:'.5px', opacity:fade(tt.isDemo) }}>{tt.value}</div>
          <div style={{ fontSize:12.5, color:'rgba(255,255,255,.85)', marginTop:10, display:'flex', flexWrap:'wrap', gap:'3px 16px' }}>
            <span style={{ opacity:fade(em.isDemo) }}>✉ {em.value}</span>
            <span style={{ opacity:fade(ph.isDemo) }}>✆ {ph.value}</span>
            <span style={{ opacity:fade(lc.isDemo) }}>⚲ {lc.value}</span>
          </div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,.7)', marginTop:4, display:'flex', flexWrap:'wrap', gap:'3px 16px' }}>
            <span style={{ opacity:fade(db.isDemo) }}>DOB: {db.value}</span>
            <span style={{ opacity:fade(nat.isDemo) }}>Nationality: {nat.value}</span>
          </div>
        </div>
      </div>
      <div style={{ padding:'30px 44px 50px' }}>
        {sm.value && <div style={{ marginBottom:22 }}><div style={secTtl}><span style={bar} />Profile</div><p style={{ fontSize:13.5, color:grey, margin:0, opacity:fade(sm.isDemo) }}>{sm.value}</p></div>}

        {exp.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}><span style={bar} />Experience</div>
          {exp.map(({row:ex, isDemo}, i) => (
            <div key={i} style={{ marginBottom:15, paddingLeft:14, borderLeft:`2px solid ${line}`, opacity:fade(isDemo) }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                <span style={{ fontSize:14.5, fontWeight:700, color:ink }}>{ex.role}</span>
                <span style={{ fontSize:12, color:light }}>{ex.duration}</span>
              </div>
              {ex.company && <div style={{ fontSize:13, color:accent, fontWeight:600, marginBottom:3 }}>{ex.company}</div>}
              {ex.desc && <div style={{ fontSize:13, color:grey }}>{ex.desc}</div>}
            </div>
          ))}
        </div>}

        {edu.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}><span style={bar} />Education</div>
          {edu.map(({row:ed, isDemo}, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10, opacity:fade(isDemo) }}>
              <div><div style={{ fontSize:14, fontWeight:600, color:ink }}>{ed.degree}</div><div style={{ fontSize:13, color:light }}>{ed.institute}</div></div>
              <div style={{ textAlign:'right' }}>{ed.year && <div style={{ fontSize:12, color:light }}>{ed.year}</div>}{ed.score && <div style={{ fontSize:12, color:accent, fontWeight:600 }}>{ed.score}</div>}</div>
            </div>
          ))}
        </div>}

        {proj.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}><span style={bar} />Projects</div>
          {proj.map(({row:pr, isDemo}, i) => (
            <div key={i} style={{ marginBottom:13, opacity:fade(isDemo) }}>
              <div style={{ fontSize:14, fontWeight:700, color:ink }}>{pr.name}</div>
              {pr.desc && <div style={{ fontSize:13, color:grey }}>{pr.desc}</div>}
              {pr.link && <div style={{ fontSize:12.5, color:accent, fontWeight:600, marginTop:2 }}>{pr.link}</div>}
            </div>
          ))}
        </div>}

        {cert.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}><span style={bar} />Certificates</div>
          {cert.map(({row:ct, isDemo}, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10, opacity:fade(isDemo) }}>
              <div><div style={{ fontSize:14, fontWeight:600, color:ink }}>{ct.name}</div>{ct.code && <div style={{ fontSize:12, color:light }}>ID: {ct.code}</div>}</div>
              {ct.year && <div style={{ fontSize:12, color:light }}>{ct.year}</div>}
            </div>
          ))}
        </div>}

        {(ws.value || gh.value || li.value) && <div style={{ marginBottom:22 }}><div style={secTtl}><span style={bar} />Links</div>
          <div style={{ fontSize:13, color:grey, marginBottom:3, opacity:fade(linksDemo) }}><b style={{ color:ink }}>Website:</b> {ws.value}</div>
          <div style={{ fontSize:13, color:grey, marginBottom:3, opacity:fade(linksDemo) }}><b style={{ color:ink }}>GitHub:</b> {gh.value}</div>
          <div style={{ fontSize:13, color:grey, marginBottom:3, opacity:fade(linksDemo) }}><b style={{ color:ink }}>LinkedIn:</b> {li.value}</div>
        </div>}

        {skillList.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}><span style={bar} />Skills</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, justifyContent:'center', opacity:fade(skillsAreDemo) }}>
            {skillList.map((s, i) => (<span key={i} style={{ fontSize:12.5, color:navy, fontWeight:600, background:'#F1F5F9', border:`1px solid #DCE3EB`, padding:'4px 12px', borderRadius:20, textAlign:'center', display:'inline-block' }}>{s}</span>))}
          </div>
        </div>}

        {hobbyList.length > 0 && <div><div style={secTtl}><span style={bar} />Hobbies & Interests</div>
          <div style={{ fontSize:13, color:grey, opacity:fade(hobbiesAreDemo) }}>{hobbyList.join(' · ')}</div>
        </div>}
      </div>
      <div style={{ position:'absolute', bottom:14, right:20, fontSize:11, color:'#C2CEDB', fontWeight:600, letterSpacing:'.5px' }}>Made with Devlayar</div>
    </div>
  );
}

/* ============================ TEMPLATE 3: MODERN ============================ */
function ModernTemplate({ data, accentColor, skillList, skillsAreDemo, hobbyList, hobbiesAreDemo }) {
  const side='#1E3A5F', ink='#1F2937', grey='#4B5563', light='#6B7280', line='#E5E7EB';
  const accent = accentColor;
  const F = "'Sora',-apple-system,sans-serif";
  const nm = show(data.name, DEMO.name), tt = show(data.title, DEMO.title);
  const em = show(data.email, DEMO.email), ph = show(data.phone, DEMO.phone), lc = show(data.location, DEMO.location);
  const db = show(data.dob, DEMO.dob), nat = show(data.nationality, DEMO.nationality);
  const sm = show(data.summary, DEMO.summary);
  const ws = show(data.website, DEMO.website), gh = show(data.github, DEMO.github), li = show(data.linkedin, DEMO.linkedin);
  const linksDemo = !(data.website.trim() || data.github.trim() || data.linkedin.trim());
  const exp = rowsOrDemo(data.experience, DEMO.experience, ['role','company','duration','desc']);
  const edu = rowsOrDemo(data.education, DEMO.education, ['degree','institute','year','score']);
  const cert = rowsOrDemo(data.certificates, DEMO.certificates, ['name','year','code']);
  const proj = rowsOrDemo(data.projects, DEMO.projects, ['name','desc','link']);

  const sideTtl = { fontSize:11, fontWeight:700, color:accent, textTransform:'uppercase', letterSpacing:'1.5px', margin:'22px 0 10px' };
  const mainTtl = { fontSize:13, fontWeight:700, color:side, textTransform:'uppercase', letterSpacing:'1.5px', margin:'0 0 12px', borderBottom:`2px solid ${accent}`, paddingBottom:6, display:'inline-block' };
  return (
    <div style={{ position:'relative', overflow:'hidden', background:'#fff', width:'100%', maxWidth:800, margin:'0 auto', color:ink, fontFamily:F, lineHeight:1.6, borderRadius:12, border:`1.5px solid ${line}`, boxShadow:'0 4px 6px rgba(0,0,0,.06)', display:'flex', alignItems:'stretch' }}>
      <div style={{ width:'34%', background:side, color:'#fff', padding:'34px 22px 50px' }}>
        {data.photo && <img src={data.photo} alt="" style={{ width:96, height:96, borderRadius:'50%', objectFit:'cover', display:'block', margin:'0 auto 16px', border:'3px solid rgba(255,255,255,.25)' }} />}
        <div style={{ fontSize:22, fontWeight:700, textAlign:'center', lineHeight:1.25, marginBottom:4, opacity:fade(nm.isDemo) }}>{nm.value}</div>
        <div style={{ fontSize:12.5, color:accent, textAlign:'center', fontWeight:600, opacity:fade(tt.isDemo) }}>{tt.value}</div>

        <div style={sideTtl}>Contact</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,.9)', lineHeight:1.9, wordBreak:'break-word' }}>
          <div style={{ opacity:fade(em.isDemo) }}>{em.value}</div>
          <div style={{ opacity:fade(ph.isDemo) }}>{ph.value}</div>
          <div style={{ opacity:fade(lc.isDemo) }}>{lc.value}</div>
        </div>

        {(db.value || nat.value) && <><div style={sideTtl}>Details</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,.9)', lineHeight:1.9 }}>
          {db.value && <div style={{ opacity:fade(db.isDemo) }}>DOB: {db.value}</div>}
          {nat.value && <div style={{ opacity:fade(nat.isDemo) }}>Nationality: {nat.value}</div>}
        </div></>}

        {(ws.value || gh.value || li.value) && <><div style={sideTtl}>Links</div>
        <div style={{ fontSize:11.5, color:'rgba(255,255,255,.9)', lineHeight:1.7, wordBreak:'break-word' }}>
          {ws.value && <div style={{ marginBottom:5, opacity:fade(linksDemo) }}><span style={{ color:accent, fontWeight:600 }}>Website:</span> {ws.value}</div>}
          {gh.value && <div style={{ marginBottom:5, opacity:fade(linksDemo) }}><span style={{ color:accent, fontWeight:600 }}>GitHub:</span> {gh.value}</div>}
          {li.value && <div style={{ marginBottom:5, opacity:fade(linksDemo) }}><span style={{ color:accent, fontWeight:600 }}>LinkedIn:</span> {li.value}</div>}
        </div></>}

        {skillList.length > 0 && <><div style={{ ...sideTtl, textAlign:'center' }}>Skills</div>
        <div style={{ fontSize:12.5, color:'rgba(255,255,255,.92)', lineHeight:2, opacity:fade(skillsAreDemo) }}>
          {skillList.map((s, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:accent, flexShrink:0 }} />
              <span>{s}</span>
            </div>
          ))}
        </div></>}

        {hobbyList.length > 0 && <><div style={sideTtl}>Interests</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,.9)', lineHeight:1.9, opacity:fade(hobbiesAreDemo) }}>
          {hobbyList.map((h, i) => <div key={i}>{h}</div>)}
        </div></>}
      </div>

      <div style={{ flex:1, padding:'34px 30px 50px' }}>
        {sm.value && <div style={{ marginBottom:22 }}><div style={mainTtl}>Profile</div><p style={{ fontSize:13.5, color:grey, margin:0, opacity:fade(sm.isDemo) }}>{sm.value}</p></div>}

        {exp.length > 0 && <div style={{ marginBottom:22 }}><div style={mainTtl}>Experience</div>
          {exp.map(({row:ex, isDemo}, i) => (
            <div key={i} style={{ marginBottom:15, opacity:fade(isDemo) }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                <span style={{ fontSize:14.5, fontWeight:700, color:ink }}>{ex.role}</span>
                <span style={{ fontSize:12, color:light }}>{ex.duration}</span>
              </div>
              {ex.company && <div style={{ fontSize:13, color:accent, fontWeight:600, marginBottom:3 }}>{ex.company}</div>}
              {ex.desc && <div style={{ fontSize:13, color:grey }}>{ex.desc}</div>}
            </div>
          ))}
        </div>}

        {proj.length > 0 && <div style={{ marginBottom:22 }}><div style={mainTtl}>Projects</div>
          {proj.map(({row:pr, isDemo}, i) => (
            <div key={i} style={{ marginBottom:13, opacity:fade(isDemo) }}>
              <div style={{ fontSize:14, fontWeight:700, color:ink }}>{pr.name}</div>
              {pr.desc && <div style={{ fontSize:13, color:grey }}>{pr.desc}</div>}
              {pr.link && <div style={{ fontSize:12.5, color:accent, fontWeight:600, marginTop:2 }}>{pr.link}</div>}
            </div>
          ))}
        </div>}

        {edu.length > 0 && <div style={{ marginBottom:22 }}><div style={mainTtl}>Education</div>
          {edu.map(({row:ed, isDemo}, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10, opacity:fade(isDemo) }}>
              <div><div style={{ fontSize:14, fontWeight:600, color:ink }}>{ed.degree}</div><div style={{ fontSize:13, color:light }}>{ed.institute}</div></div>
              <div style={{ textAlign:'right' }}>{ed.year && <div style={{ fontSize:12, color:light }}>{ed.year}</div>}{ed.score && <div style={{ fontSize:12, color:accent, fontWeight:600 }}>{ed.score}</div>}</div>
            </div>
          ))}
        </div>}

        {cert.length > 0 && <div><div style={mainTtl}>Certificates</div>
          {cert.map(({row:ct, isDemo}, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10, opacity:fade(isDemo) }}>
              <div><div style={{ fontSize:14, fontWeight:600, color:ink }}>{ct.name}</div>{ct.code && <div style={{ fontSize:12, color:light }}>ID: {ct.code}</div>}</div>
              {ct.year && <div style={{ fontSize:12, color:light }}>{ct.year}</div>}
            </div>
          ))}
        </div>}
      </div>
      <div style={{ position:'absolute', bottom:14, right:20, fontSize:11, color:'#C2CEDB', fontWeight:600, letterSpacing:'.5px' }}>Made with Devlayar</div>
    </div>
  );
}

/* ============================ TEMPLATE 4: MINIMAL ============================ */
function MinimalTemplate({ data, accentColor, skillList, skillsAreDemo, hobbyList, hobbiesAreDemo }) {
  const ink='#111827', grey='#4B5563', light='#6B7280', line='#E5E7EB';
  const A = accentColor;
  const F = "'Sora',-apple-system,sans-serif";
  const nm = show(data.name, DEMO.name), tt = show(data.title, DEMO.title);
  const em = show(data.email, DEMO.email), ph = show(data.phone, DEMO.phone), lc = show(data.location, DEMO.location);
  const sm = show(data.summary, DEMO.summary);
  const ws = show(data.website, DEMO.website), gh = show(data.github, DEMO.github), li = show(data.linkedin, DEMO.linkedin);
  const linksDemo = !(data.website.trim() || data.github.trim() || data.linkedin.trim());
  const exp = rowsOrDemo(data.experience, DEMO.experience, ['role','company','duration','desc']);
  const edu = rowsOrDemo(data.education, DEMO.education, ['degree','institute','year','score']);
  const cert = rowsOrDemo(data.certificates, DEMO.certificates, ['name','year','code']);
  const proj = rowsOrDemo(data.projects, DEMO.projects, ['name','desc','link']);

  const secTtl = { fontSize:11, fontWeight:700, color:A, textTransform:'uppercase', letterSpacing:'3px', margin:'0 0 12px' };
  return (
    <div style={{ position:'relative', overflow:'hidden', background:'#fff', width:'100%', maxWidth:800, margin:'0 auto', color:ink, fontFamily:F, lineHeight:1.65, borderRadius:12, border:`1.5px solid ${line}`, boxShadow:'0 4px 6px rgba(0,0,0,.06)', padding:'48px 46px 56px' }}>
      <div style={{ textAlign:'center', marginBottom:6 }}>
        {data.photo && <img src={data.photo} alt="" style={{ width:80, height:80, borderRadius:'50%', objectFit:'cover', margin:'0 auto 14px', display:'block', border:`2px solid ${line}` }} />}
        <div style={{ fontSize:30, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', opacity:fade(nm.isDemo) }}>{nm.value}</div>
        <div style={{ fontSize:14, color:A, fontWeight:600, letterSpacing:'1px', marginTop:4, opacity:fade(tt.isDemo) }}>{tt.value}</div>
        <div style={{ fontSize:12.5, color:light, marginTop:10, display:'flex', justifyContent:'center', flexWrap:'wrap', gap:'2px 14px' }}>
          <span style={{ opacity:fade(em.isDemo) }}>{em.value}</span>
          <span style={{ opacity:fade(ph.isDemo) }}>{ph.value}</span>
          <span style={{ opacity:fade(lc.isDemo) }}>{lc.value}</span>
        </div>
      </div>
      <div style={{ height:1, background:line, margin:'22px 0' }} />

      {sm.value && <div style={{ marginBottom:22 }}><div style={secTtl}>Summary</div><p style={{ fontSize:13.5, color:grey, margin:0, opacity:fade(sm.isDemo) }}>{sm.value}</p></div>}

      {exp.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Experience</div>
        {exp.map(({row:ex, isDemo}, i) => (
          <div key={i} style={{ marginBottom:15, opacity:fade(isDemo) }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
              <span style={{ fontSize:14, fontWeight:700, color:ink }}>{ex.role}{ex.company && <span style={{ fontWeight:500, color:light }}> — {ex.company}</span>}</span>
              <span style={{ fontSize:12, color:light }}>{ex.duration}</span>
            </div>
            {ex.desc && <div style={{ fontSize:13, color:grey, marginTop:2 }}>{ex.desc}</div>}
          </div>
        ))}
      </div>}

      {proj.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Projects</div>
        {proj.map(({row:pr, isDemo}, i) => (
          <div key={i} style={{ marginBottom:12, opacity:fade(isDemo) }}>
            <div style={{ fontSize:14, fontWeight:700, color:ink }}>{pr.name}{pr.link && <span style={{ fontSize:12.5, fontWeight:500, color:A }}> · {pr.link}</span>}</div>
            {pr.desc && <div style={{ fontSize:13, color:grey, marginTop:2 }}>{pr.desc}</div>}
          </div>
        ))}
      </div>}

      {edu.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Education</div>
        {edu.map(({row:ed, isDemo}, i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8, opacity:fade(isDemo) }}>
            <span style={{ fontSize:13.5, color:ink }}><b>{ed.degree}</b>{ed.institute && <span style={{ color:light }}> — {ed.institute}</span>}</span>
            <span style={{ fontSize:12, color:light, whiteSpace:'nowrap' }}>{ed.year}{ed.score && ` · ${ed.score}`}</span>
          </div>
        ))}
      </div>}

      {cert.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Certificates</div>
        {cert.map(({row:ct, isDemo}, i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8, opacity:fade(isDemo) }}>
            <span style={{ fontSize:13.5, color:ink }}><b>{ct.name}</b>{ct.code && <span style={{ color:light }}> — ID: {ct.code}</span>}</span>
            <span style={{ fontSize:12, color:light }}>{ct.year}</span>
          </div>
        ))}
      </div>}

      {skillList.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Skills</div>
        <div style={{ fontSize:13, color:grey, opacity:fade(skillsAreDemo) }}>{skillList.join('  ·  ')}</div>
      </div>}

      {(ws.value || gh.value || li.value) && <div style={{ marginBottom:22 }}><div style={secTtl}>Links</div>
        <div style={{ fontSize:13, color:grey, opacity:fade(linksDemo), display:'flex', flexWrap:'wrap', gap:'2px 16px' }}>
          <span>{ws.value}</span><span>{gh.value}</span><span>{li.value}</span>
        </div>
      </div>}

      {hobbyList.length > 0 && <div><div style={secTtl}>Interests</div>
        <div style={{ fontSize:13, color:grey, opacity:fade(hobbiesAreDemo) }}>{hobbyList.join('  ·  ')}</div>
      </div>}
      <div style={{ position:'absolute', bottom:16, right:20, fontSize:11, color:'#C2CEDB', fontWeight:600, letterSpacing:'.5px' }}>Made with Devlayar</div>
    </div>
  );
}

/* ============================ TEMPLATE 5: ELEGANT ============================ */
function ElegantTemplate({ data, accentColor, skillList, skillsAreDemo, hobbyList, hobbiesAreDemo }) {
  const ink='#1C1917', grey='#44403C', light='#78716C', line='#E7E5E4', cream='#FAF9F7';
  const A = accentColor;
  const serif = "Georgia,'Times New Roman',serif";
  const F = "'Sora',-apple-system,sans-serif";
  const nm = show(data.name, DEMO.name), tt = show(data.title, DEMO.title);
  const em = show(data.email, DEMO.email), ph = show(data.phone, DEMO.phone), lc = show(data.location, DEMO.location);
  const db = show(data.dob, DEMO.dob), nat = show(data.nationality, DEMO.nationality);
  const sm = show(data.summary, DEMO.summary);
  const ws = show(data.website, DEMO.website), gh = show(data.github, DEMO.github), li = show(data.linkedin, DEMO.linkedin);
  const linksDemo = !(data.website.trim() || data.github.trim() || data.linkedin.trim());
  const exp = rowsOrDemo(data.experience, DEMO.experience, ['role','company','duration','desc']);
  const edu = rowsOrDemo(data.education, DEMO.education, ['degree','institute','year','score']);
  const cert = rowsOrDemo(data.certificates, DEMO.certificates, ['name','year','code']);
  const proj = rowsOrDemo(data.projects, DEMO.projects, ['name','desc','link']);

  const secTtl = { fontSize:15, fontWeight:700, color:A, fontFamily:serif, fontStyle:'italic', margin:'0 0 12px', display:'flex', alignItems:'center', gap:10 };
  const rule = { flex:1, height:1, background:line };
  return (
    <div style={{ position:'relative', overflow:'hidden', background:'#fff', width:'100%', maxWidth:800, margin:'0 auto', color:ink, fontFamily:F, lineHeight:1.65, borderRadius:12, border:`1.5px solid ${line}`, boxShadow:'0 4px 6px rgba(0,0,0,.06)' }}>
      <div style={{ background:cream, padding:'40px 48px 32px', textAlign:'center', borderBottom:`3px double ${A}` }}>
        {data.photo && <img src={data.photo} alt="" style={{ width:88, height:88, borderRadius:'50%', objectFit:'cover', margin:'0 auto 16px', display:'block', border:`3px solid #fff`, boxShadow:'0 2px 8px rgba(0,0,0,.12)' }} />}
        <div style={{ fontSize:34, fontWeight:700, fontFamily:serif, letterSpacing:'.5px', opacity:fade(nm.isDemo) }}>{nm.value}</div>
        <div style={{ fontSize:14, color:A, fontWeight:600, letterSpacing:'3px', textTransform:'uppercase', marginTop:6, opacity:fade(tt.isDemo) }}>{tt.value}</div>
        <div style={{ fontSize:12.5, color:light, marginTop:12, display:'flex', justifyContent:'center', flexWrap:'wrap', gap:'2px 14px' }}>
          <span style={{ opacity:fade(em.isDemo) }}>{em.value}</span>
          <span style={{ opacity:fade(ph.isDemo) }}>· {ph.value}</span>
          <span style={{ opacity:fade(lc.isDemo) }}>· {lc.value}</span>
        </div>
      </div>

      <div style={{ padding:'32px 48px 52px' }}>
        {sm.value && <div style={{ marginBottom:22 }}><div style={secTtl}>Profile<span style={rule} /></div><p style={{ fontSize:13.5, color:grey, margin:0, opacity:fade(sm.isDemo) }}>{sm.value}</p></div>}

        {exp.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Experience<span style={rule} /></div>
          {exp.map(({row:ex, isDemo}, i) => (
            <div key={i} style={{ marginBottom:15, opacity:fade(isDemo) }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                <span style={{ fontSize:14.5, fontWeight:700, color:ink, fontFamily:serif }}>{ex.role}</span>
                <span style={{ fontSize:12, color:light, fontStyle:'italic' }}>{ex.duration}</span>
              </div>
              {ex.company && <div style={{ fontSize:13, color:A, fontWeight:600, marginBottom:3 }}>{ex.company}</div>}
              {ex.desc && <div style={{ fontSize:13, color:grey }}>{ex.desc}</div>}
            </div>
          ))}
        </div>}

        {proj.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Projects<span style={rule} /></div>
          {proj.map(({row:pr, isDemo}, i) => (
            <div key={i} style={{ marginBottom:12, opacity:fade(isDemo) }}>
              <div style={{ fontSize:14, fontWeight:700, color:ink, fontFamily:serif }}>{pr.name}</div>
              {pr.desc && <div style={{ fontSize:13, color:grey }}>{pr.desc}</div>}
              {pr.link && <div style={{ fontSize:12.5, color:A, fontWeight:600, marginTop:2 }}>{pr.link}</div>}
            </div>
          ))}
        </div>}

        {edu.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Education<span style={rule} /></div>
          {edu.map(({row:ed, isDemo}, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:9, opacity:fade(isDemo) }}>
              <div><div style={{ fontSize:14, fontWeight:600, color:ink, fontFamily:serif }}>{ed.degree}</div><div style={{ fontSize:13, color:light }}>{ed.institute}</div></div>
              <div style={{ textAlign:'right' }}>{ed.year && <div style={{ fontSize:12, color:light }}>{ed.year}</div>}{ed.score && <div style={{ fontSize:12, color:A, fontWeight:600 }}>{ed.score}</div>}</div>
            </div>
          ))}
        </div>}

        {cert.length > 0 && <div style={{ marginBottom:22 }}><div style={secTtl}>Certificates<span style={rule} /></div>
          {cert.map(({row:ct, isDemo}, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:9, opacity:fade(isDemo) }}>
              <div><div style={{ fontSize:14, fontWeight:600, color:ink, fontFamily:serif }}>{ct.name}</div>{ct.code && <div style={{ fontSize:12, color:light }}>ID: {ct.code}</div>}</div>
              {ct.year && <div style={{ fontSize:12, color:light }}>{ct.year}</div>}
            </div>
          ))}
        </div>}

        <div style={{ display:'flex', gap:40, flexWrap:'wrap' }}>
          <div style={{ flex:'1 1 200px' }}><div style={secTtl}>Skills<span style={rule} /></div>
            <div style={{ fontSize:13, color:grey, opacity:fade(skillsAreDemo) }}>{skillList.join('  ·  ')}</div>
          </div>
          <div style={{ flex:'1 1 200px' }}><div style={secTtl}>Interests<span style={rule} /></div>
            <div style={{ fontSize:13, color:grey, opacity:fade(hobbiesAreDemo) }}>{hobbyList.join('  ·  ')}</div>
          </div>
        </div>

        {(ws.value || gh.value || li.value) && <div style={{ marginTop:22 }}><div style={secTtl}>Links<span style={rule} /></div>
          <div style={{ fontSize:13, color:grey, opacity:fade(linksDemo), display:'flex', flexWrap:'wrap', gap:'2px 16px' }}>
            <span><b style={{ color:ink }}>Website:</b> {ws.value}</span>
            <span><b style={{ color:ink }}>GitHub:</b> {gh.value}</span>
            <span><b style={{ color:ink }}>LinkedIn:</b> {li.value}</span>
          </div>
        </div>}
      </div>
      <div style={{ position:'absolute', bottom:16, right:20, fontSize:11, color:'#C2CEDB', fontWeight:600, letterSpacing:'.5px' }}>Made with Devlayar</div>
    </div>
  );
}

/* ============================ TEMPLATE 6: ATS ============================ */
/* Plain, single-column, no colors or graphics. Built for resume-screening
   software (ATS): simple black text, standard section headings, no columns,
   no photo, no icons. The accent picker is hidden for this template. */
function AtsTemplate({ data, skillList, skillsAreDemo, hobbyList, hobbiesAreDemo }) {
  const ink='#000000', F = "Arial,'Helvetica Neue',sans-serif";
  const nm = show(data.name, DEMO.name), tt = show(data.title, DEMO.title);
  const em = show(data.email, DEMO.email), ph = show(data.phone, DEMO.phone), lc = show(data.location, DEMO.location);
  const sm = show(data.summary, DEMO.summary);
  const ws = show(data.website, DEMO.website), gh = show(data.github, DEMO.github), li = show(data.linkedin, DEMO.linkedin);
  const linksDemo = !(data.website.trim() || data.github.trim() || data.linkedin.trim());
  const exp = rowsOrDemo(data.experience, DEMO.experience, ['role','company','duration','desc']);
  const edu = rowsOrDemo(data.education, DEMO.education, ['degree','institute','year','score']);
  const cert = rowsOrDemo(data.certificates, DEMO.certificates, ['name','year','code']);
  const proj = rowsOrDemo(data.projects, DEMO.projects, ['name','desc','link']);

  const secTtl = { fontSize:13, fontWeight:700, color:ink, textTransform:'uppercase', letterSpacing:'.5px', margin:'0 0 8px', borderBottom:`1px solid ${ink}`, paddingBottom:3 };
  return (
    <div style={{ position:'relative', overflow:'hidden', background:'#fff', width:'100%', maxWidth:800, margin:'0 auto', color:ink, fontFamily:F, lineHeight:1.5, borderRadius:12, border:'1.5px solid #E3E8ED', boxShadow:'0 4px 6px rgba(0,0,0,.06)', padding:'44px 46px 56px' }}>
      <div style={{ fontSize:24, fontWeight:700, opacity:fade(nm.isDemo) }}>{nm.value}</div>
      <div style={{ fontSize:14, fontWeight:600, marginTop:2, opacity:fade(tt.isDemo) }}>{tt.value}</div>
      <div style={{ fontSize:12.5, marginTop:6 }}>
        <span style={{ opacity:fade(em.isDemo) }}>{em.value}</span>
        <span> | </span><span style={{ opacity:fade(ph.isDemo) }}>{ph.value}</span>
        <span> | </span><span style={{ opacity:fade(lc.isDemo) }}>{lc.value}</span>
      </div>
      <div style={{ fontSize:12.5, marginTop:2, opacity:fade(linksDemo) }}>
        {ws.value} | {gh.value} | {li.value}
      </div>

      {sm.value && <div style={{ marginTop:20 }}><div style={secTtl}>Professional Summary</div>
        <p style={{ fontSize:12.5, margin:0, opacity:fade(sm.isDemo) }}>{sm.value}</p>
      </div>}

      {exp.length > 0 && <div style={{ marginTop:18 }}><div style={secTtl}>Work Experience</div>
        {exp.map(({row:ex, isDemo}, i) => (
          <div key={i} style={{ marginBottom:12, opacity:fade(isDemo) }}>
            <div style={{ fontSize:13, fontWeight:700 }}>{ex.role}{ex.company && `, ${ex.company}`}</div>
            {ex.duration && <div style={{ fontSize:12, fontStyle:'italic' }}>{ex.duration}</div>}
            {ex.desc && <div style={{ fontSize:12.5, marginTop:2 }}>{ex.desc}</div>}
          </div>
        ))}
      </div>}

      {proj.length > 0 && <div style={{ marginTop:18 }}><div style={secTtl}>Projects</div>
        {proj.map(({row:pr, isDemo}, i) => (
          <div key={i} style={{ marginBottom:11, opacity:fade(isDemo) }}>
            <div style={{ fontSize:13, fontWeight:700 }}>{pr.name}</div>
            {pr.desc && <div style={{ fontSize:12.5 }}>{pr.desc}</div>}
            {pr.link && <div style={{ fontSize:12 }}>{pr.link}</div>}
          </div>
        ))}
      </div>}

      {edu.length > 0 && <div style={{ marginTop:18 }}><div style={secTtl}>Education</div>
        {edu.map(({row:ed, isDemo}, i) => (
          <div key={i} style={{ marginBottom:8, opacity:fade(isDemo) }}>
            <div style={{ fontSize:13, fontWeight:700 }}>{ed.degree}</div>
            <div style={{ fontSize:12.5 }}>{ed.institute}{ed.year && `, ${ed.year}`}{ed.score && ` — ${ed.score}`}</div>
          </div>
        ))}
      </div>}

      {cert.length > 0 && <div style={{ marginTop:18 }}><div style={secTtl}>Certifications</div>
        {cert.map(({row:ct, isDemo}, i) => (
          <div key={i} style={{ fontSize:12.5, marginBottom:5, opacity:fade(isDemo) }}>
            {ct.name}{ct.year && `, ${ct.year}`}{ct.code && ` (ID: ${ct.code})`}
          </div>
        ))}
      </div>}

      {skillList.length > 0 && <div style={{ marginTop:18 }}><div style={secTtl}>Skills</div>
        <div style={{ fontSize:12.5, opacity:fade(skillsAreDemo) }}>{skillList.join(', ')}</div>
      </div>}

      {hobbyList.length > 0 && <div style={{ marginTop:18 }}><div style={secTtl}>Interests</div>
        <div style={{ fontSize:12.5, opacity:fade(hobbiesAreDemo) }}>{hobbyList.join(', ')}</div>
      </div>}
      <div style={{ position:'absolute', bottom:16, right:20, fontSize:11, color:'#C2CEDB', fontWeight:600, letterSpacing:'.5px' }}>Made with Devlayar</div>
    </div>
  );
}